import { createContext, useCallback, useContext, useState } from 'react';
import type { ReactNode } from 'react';

export type ToastType = 'success' | 'error' | 'info';

interface ToastItem { id: number; message: string; type: ToastType; }
interface ToastContextValue { toast: (message: string, type?: ToastType) => void; }

const Ctx = createContext<ToastContextValue>({ toast: () => {} });

export function useToast() { return useContext(Ctx); }

let _seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++_seq;
    setItems(prev => [...prev, { id, message, type }]);
    setTimeout(() => setItems(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const dismiss = (id: number) => setItems(prev => prev.filter(t => t.id !== id));

  const bg: Record<ToastType, string> = {
    success: 'bg-[#1A7A3C]',
    error:   'bg-[#A32D2D]',
    info:    'bg-[#010E23]',
  };
  const icon: Record<ToastType, string> = { success: '✓', error: '✕', info: 'ℹ' };

  return (
    <Ctx.Provider value={{ toast }}>
      {children}

      {/* Toast container — fixed bottom-right */}
      <div
        className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full pointer-events-none"
        aria-live="polite"
        aria-atomic="false"
      >
        {items.map(item => (
          <div
            key={item.id}
            className={`flex items-start gap-3 rounded-xl px-4 py-3 shadow-xl text-white pointer-events-auto
              ${bg[item.type]} animate-[toast-in_0.2s_ease-out]`}
          >
            <span className="text-sm font-bold mt-0.5 shrink-0">{icon[item.type]}</span>
            <span className="flex-1 text-sm leading-snug">{item.message}</span>
            <button
              onClick={() => dismiss(item.id)}
              className="shrink-0 text-white/60 hover:text-white leading-none text-lg transition-colors"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes toast-in {
          from { transform: translateX(110%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </Ctx.Provider>
  );
}
